import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

function initials(name) {
  if (!name) return "?";
  return name.split(" ").slice(0, 2).map(w => (w[0] || "").toUpperCase()).join("");
}

function resolveSla(sla) {
  if (!sla) return { status: "on_track", label: null };
  if (typeof sla === "string") return { status: sla, label: null };
  return { status: sla.status || "on_track", label: sla.label || null };
}

const SLA_TEXT_CLASS = {
  breached: "kb-sla-text-breached",
  at_risk:  "kb-sla-text-at-risk",
  on_track: "kb-sla-text-on-track",
  no_sla:   "kb-sla-text-no-sla",
};

const SLA_BADGE_CLASS = {
  breached: "kb-sla-badge kb-sla-breached",
  at_risk:  "kb-sla-badge kb-sla-at-risk",
  on_track: "kb-sla-badge kb-sla-on-track",
  no_sla:   "kb-sla-badge kb-sla-no-sla",
};

const SLA_ICON_COLOR = {
  breached: "var(--t2t-danger)",
  at_risk:  "var(--t2t-warning)",
  on_track: "var(--t2t-success)",
  no_sla:   "var(--t2t-text-muted)",
};

const SLA_LABEL = {
  breached: "Breached",
  at_risk:  "At Risk",
  on_track: "On Track",
  no_sla:   "No SLA",
};

function priorityClass(p) {
  if (p === "critical") return "kb-priority-pill kb-priority-critical";
  if (p === "high")     return "kb-priority-pill kb-priority-high";
  if (p === "medium")   return "kb-priority-pill kb-priority-medium";
  return "kb-priority-pill kb-priority-low";
}

function priorityLabel(p) {
  if (!p) return "Low";
  return p.charAt(0).toUpperCase() + p.slice(1);
}

// ─── Confirmation Modal ───────────────────────────────────────────
const ACTION_CFG = {
  approve: { label: "Approve",      icon: "bi-check-circle-fill",  iconColor: "var(--t2t-success)", btnClass: "btn btn-sm",  btnStyle: { background: "var(--t2t-success)", color: "#fff", border: "none" }, body: "This will move the request to the next stage." },
  reject:  { label: "Reject",       icon: "bi-x-circle-fill",      iconColor: "var(--t2t-danger)",  btnClass: "btn btn-sm btn-danger", btnStyle: {}, body: "This will return the request to the first stage for correction." },
  hold:    { label: "Put on Hold",   icon: "bi-pause-circle-fill",  iconColor: "var(--t2t-warning)", btnClass: "btn btn-sm", btnStyle: { background: "var(--t2t-warning)", color: "#fff", border: "none" }, body: "The request will be paused and marked as on hold." },
};

function ConfirmActionModal({ action, refId, onConfirm, onCancel }) {
  const cfg = ACTION_CFG[action] || ACTION_CFG.approve;
  return (
    <div
      className="kb-modal-overlay"
      onMouseDown={e => e.stopPropagation()}
      onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="kb-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="kb-modal-header">
          <div className="kb-modal-title">
            <i className={`bi ${cfg.icon}`} style={{ fontSize: 16, color: cfg.iconColor }} />
            Confirm {cfg.label}
          </div>
          <button
            className="btn-close btn-close-sm"
            onClick={onCancel}
            aria-label="Close"
            style={{ fontSize: 11 }}
          />
        </div>
        {/* Body */}
        <div className="kb-modal-body">
          <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: refId ? 8 : 0 }}>
            {cfg.body}
          </p>
          {refId && (
            <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 0 }}>
              Request:&nbsp;
              <span className="font-monospace fw-semibold" style={{ color: "var(--t2t-text-primary)" }}>
                {refId}
              </span>
            </p>
          )}
        </div>
        {/* Footer */}
        <div className="kb-modal-footer">
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: 13 }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={cfg.btnClass}
            style={{ fontSize: 13, ...cfg.btnStyle }}
            onClick={onConfirm}
          >
            <i className={`bi ${cfg.icon} me-1`} />
            Yes, {cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
}

ConfirmActionModal.propTypes = {
  action:    PropTypes.string.isRequired,
  refId:     PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel:  PropTypes.func.isRequired,
};

// ─── Kanban Card ──────────────────────────────────────────────────
export default function KanbanCard({ card, laneId, onAction, onCardClick, chatUnreadCount }) {
  const [pendingAction, setPendingAction] = useState(null);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: String(card.request_id),
    data: { card, laneId },
    disabled: !card.can_act,
  });

  const { status: slaStatus, label: slaLabel } = resolveSla(card.sla);
  const isBreached = slaStatus === "breached";
  const isAtRisk   = slaStatus === "at_risk";

  const cardClasses = [
    "kb-card",
    card.can_act  ? "can-act"      : "",
    isDragging    ? "is-dragging"  : "",
    isBreached    ? "sla-breached" : "",
    isAtRisk && !isBreached ? "sla-at-risk" : "",
  ].filter(Boolean).join(" ");

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const visibleTags = (card.tags || [])
    .filter(t => (typeof t === "object" ? t.value : t))
    .slice(0, 3);

  const handleCardDoubleClick = (e) => {
    if (e.defaultPrevented || pendingAction) return;
    if (onCardClick) onCardClick(card.request_id);
  };

  const slaDisplayLabel = slaLabel
    ? (isBreached ? `−${slaLabel.replace("h overdue", "h")}` : slaLabel)
    : SLA_LABEL[slaStatus] || "On Track";

  return (
    <div
      ref={setNodeRef}
      className={cardClasses}
      style={style}
      onDoubleClick={handleCardDoubleClick}
      title="Double-click to view details"
      {...(card.can_act ? { ...listeners, ...attributes } : {})}
    >
      {/* Row 1: ref # + badges */}
      <div className="d-flex align-items-center justify-content-between gap-1">
        <span className="kb-card-ref">{card.ref_id}</span>
        <div className="d-flex align-items-center gap-1">
          {card.request_status === "on_hold" && (
            <span className="kb-on-hold-pill">On Hold</span>
          )}
          <span className={priorityClass(card.priority)}>
            {priorityLabel(card.priority)}
          </span>
        </div>
      </div>

      {/* Title */}
      <div className="kb-card-title" style={{ display: "flex", alignItems: "center" }}>
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{card.title}</span>
        {chatUnreadCount > 0 && (
          <span className="kb-card-chat-dot" title={`${chatUnreadCount} new chat message${chatUnreadCount > 1 ? "s" : ""}`}>
            {chatUnreadCount}
          </span>
        )}
      </div>

      {/* Tag chips */}
      {visibleTags.length > 0 && (
        <div className="kb-card-tags">
          {visibleTags.map((t, i) => (
            <span key={i} className="kb-card-tag">
              {typeof t === "object" ? (t.value ?? t.key) : t}
            </span>
          ))}
        </div>
      )}

      {/* SLA row */}
      <div className="kb-card-sla-row">
        <i
          className="bi bi-clock"
          style={{ fontSize: 11, color: SLA_ICON_COLOR[slaStatus] || SLA_ICON_COLOR.no_sla }}
        />
        <span className={`kb-card-sla-text ${SLA_TEXT_CLASS[slaStatus] || "kb-sla-text-no-sla"}`}>
          {slaDisplayLabel}
        </span>
      </div>

      {/* Footer: assignee + SLA badge — BUG-61: role-name fallback when unassigned */}
      <div className="kb-card-footer">
        <div className="kb-card-assignee">
          <div className={`kb-card-avatar${card.assignee_name ? "" : " unassigned"}`}>
            {initials(card.assignee_name)}
          </div>
          {!card.assignee_name && (() => {
            const roles = Array.isArray(card.stage_role_names) ? card.stage_role_names.filter(Boolean) : [];
            if (roles.length > 0) {
              return (
                <span
                  className="kb-card-unassigned"
                  style={{ fontStyle: "italic" }}
                  title={`Anyone with role: ${roles.join(", ")}`}
                >
                  Role: {roles.join(", ")}
                </span>
              );
            }
            return <span className="kb-card-unassigned">Unassigned</span>;
          })()}
        </div>
        <span className={SLA_BADGE_CLASS[slaStatus] || SLA_BADGE_CLASS.no_sla}>
          {SLA_LABEL[slaStatus] || "On Track"}
        </span>
      </div>

      {/* Action buttons — actionable cards only */}
      {card.can_act && onAction && (
        <div
          className="kb-card-actions"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <button
            className="kb-action-btn kb-action-approve"
            onClick={e => { e.stopPropagation(); setPendingAction("approve"); }}
            title="Approve"
          >
            <i className="bi bi-check-circle" />Approve
          </button>
          <button
            className="kb-action-btn kb-action-reject"
            onClick={e => { e.stopPropagation(); setPendingAction("reject"); }}
            title="Reject"
          >
            <i className="bi bi-x-circle" />Reject
          </button>
          <button
            className="kb-action-btn kb-action-hold"
            onClick={e => { e.stopPropagation(); setPendingAction("hold"); }}
            title="Put on Hold"
          >
            <i className="bi bi-pause-circle" />Hold
          </button>
        </div>
      )}

      {/* Confirmation modal */}
      {pendingAction && (
        <ConfirmActionModal
          action={pendingAction}
          refId={card.ref_id}
          onConfirm={() => { onAction(card.request_id, pendingAction); setPendingAction(null); }}
          onCancel={() => setPendingAction(null)}
        />
      )}
    </div>
  );
}

KanbanCard.propTypes = {
  card: PropTypes.shape({
    request_id:     PropTypes.number.isRequired,
    ref_id:         PropTypes.string,
    title:          PropTypes.string,
    priority:       PropTypes.string,
    request_status: PropTypes.string,
    tags:           PropTypes.array,
    assignee_name:  PropTypes.string,
    stage_role_names: PropTypes.arrayOf(PropTypes.string),
    sla:            PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    can_act:        PropTypes.bool,
  }).isRequired,
  laneId:      PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAction:    PropTypes.func,
  onCardClick: PropTypes.func,
  chatUnreadCount: PropTypes.number,
};
